<template>
    <div v-cloak>
        <v-container id="top" fluid>
            <v-row v-if="!student.StudentID">
                <v-col cols="12" class="text-center">
                    <v-progress-circular color="info" indeterminate></v-progress-circular>
                    <h3 class="mt-3">Loading&hellip;</h3>
                </v-col>
            </v-row>
            <v-row v-else>
                <v-col cols="12" md="9">
                    <h1>{{ !!StudentFirstName ? `Hi ${StudentFirstName}` : '&hellip;' }}</h1>
                    <h3 style="font-weight:normal">Welcome to Onboarding at Dundee and Angus College - your learner journey starts here&hellip;</h3>
                    <v-container id="kickers"
                                 grid-list-lg
                                 fluid
                                 class="mb-4 px-0">
                        <v-row>
                            <courses-kicker v-if="ShowCoursesKicker" :constants="constants" :student="student" :enrolments="enrolments" :enrolment-registrations="enrolmentRegistrations"></courses-kicker>
                            <funding-kicker v-if="ShowFundingKicker" :enrolments="enrolments" :bursary-applications="bursaryApplications"></funding-kicker>
                            <photo-kicker :local-settings="localSettings" :enrolment-registrations="enrolmentRegistrations" :photo-record="photoRecord" :photo="photo" :enrolments="enrolments"></photo-kicker>
                            <college-card-kicker v-if="HasActiveEnrolments" :college-card="collegeCard"></college-card-kicker>
                            <password-kicker></password-kicker>
                            <timetable-kicker v-if="ShowTimetablePanel"></timetable-kicker>
                            <induction-hub-kicker></induction-hub-kicker>
                            <myda-life-kicker></myda-life-kicker>
                        </v-row>
                        <v-row>
                            <v-col>
                                <span class="fa-stack fa-xs">
                                    <i class="fas fa-circle fa-stack-2x icon-error"></i>
                                    <i class="fas fa-exclamation fa-stack-1x"></i>
                                </span> Action required
                                <br />
                                <span class="fa-stack fa-xs">
                                    <i class="fas fa-circle fa-stack-2x icon-info"></i>
                                    <i class="fas fa-info fa-stack-1x"></i>
                                </span> For information only - no action required
                            </v-col>
                        </v-row>
                    </v-container>

                    <v-skeleton-loader v-if="loadingEnrolments" type="card, article, actions"></v-skeleton-loader>

                    <connect2-course-panel v-for="enrolment in Connect2Enrolments" :key="enrolment.EnrolmentID" :enrolment="enrolment" :enrolment-registration="EnrolmentRegistration(enrolment.EnrolmentID)"></connect2-course-panel>
                    <unite-course-panel v-for="enrolment in ActiveUNITeEnrolments" :key="enrolment.EnrolmentID" :enrolment="enrolment" :enrolment-registration="EnrolmentRegistration(enrolment.EnrolmentID)"></unite-course-panel>
                    <rems-course-panel v-for="enrolment in ActiveREMSEnrolments" :key="enrolment.EnrolmentID" :enrolment="enrolment"></rems-course-panel>

                    <he-funding-panel v-if="ShowHEFundingPanel" :student="student" :enrolments="enrolments"></he-funding-panel>
                    <fe-funding-panel v-if="ShowFEFundingPanel" :student-funding-local-settings="studentFundingLocalSettings" :enrolments="enrolments" :bursary-applications="bursaryApplications"></fe-funding-panel>
                    <pt-funding-panel v-if="ShowPTFundingPanel" :student="student" :enrolments="enrolments"></pt-funding-panel>
                    <photo-panel :local-settings="localSettings" :college-card-local-settings="collegeCardLocalSettings" :enrolment-registrations="enrolmentRegistrations" :photo-record="photoRecord" :photo="photo" :enrolments="enrolments"></photo-panel>
                    <college-card-panel v-if="HasActiveEnrolments" :college-card-local-settings="collegeCardLocalSettings" :enrolments="enrolments" :photo="photo" :college-card="collegeCard" :college-card-opt-in="collegeCardOptIn"></college-card-panel>
                    <password-panel></password-panel>
                    <timetable-panel v-if="ShowTimetablePanel" :local-settings="localSettings" :enrolments="enrolments"></timetable-panel>
                    <induction-hub-panel></induction-hub-panel>
                    <myda-life-panel></myda-life-panel>
                </v-col>
                <v-col cols="12" md="3">
                    <right-navigation></right-navigation>
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<script>
    import { eventBus } from '@/main'

    import filtersMixin from '@/views/components/mixins/base/filters.js'
    import constantsMixin from '@/views/components/mixins/enterpriseDataApi/constants.js'
    import camsConstantsMixin from '@/views/components/mixins/enterpriseDataApi/camsConstants.js'
    import appApiMixin from '@/views/components/mixins/appApi.js'
    import localSettingsMixin from '@/views/components/mixins/localSettings.js'
    import studentsMixin from '@/views/components/mixins/enterpriseDataApi/students.js'
    import enrolmentsMixin from '@/views/components/mixins/enterpriseDataApi/enrolments.js'
    import bursaryApplicationsMixin from '@/views/components/mixins/enterpriseDataApi/bursaryApplications.js'
    import enrolmentRegistrationsMixin from '@/views/components/mixins/enrolmentRegistrations.js'
    import inductionAttendanceMixin from '@/views/components/mixins/inductionAttendance.js'
    import photosMixin from '@/views/components/mixins/photos.js'
    import collegeCardsMixin from '@/views/components/mixins/collegeCards.js'
    import studentFundingMixin from '@/views/components/mixins/studentFunding.js'

    import coursesKicker from '@/views/components/kickers/CoursesKicker'
    import fundingKicker from '@/views/components/kickers/FundingKicker'
    import photoKicker from '@/views/components/kickers/PhotoKicker'
    import collegeCardKicker from '@/views/components/kickers/CollegeCardKicker'
    import passwordKicker from '@/views/components/kickers/PasswordKicker'
    import timetableKicker from '@/views/components/kickers/TimetableKicker'
    import inductionHubKicker from '@/views/components/kickers/InductionHubKicker'
    import myDALifeKicker from '@/views/components/kickers/MyDALifeKicker'

    import uniteCoursePanel from '@/views/components/panels/UNITeCoursePanel'
    import connect2CoursePanel from '@/views/components/panels/Connect2CoursePanel'
    import remsCoursePanel from '@/views/components/panels/REMSCoursePanel'
    import heFundingPanel from '@/views/components/panels/HEFundingPanel'
    import feFundingPanel from '@/views/components/panels/FEFundingPanel'
    import ptFundingPanel from '@/views/components/panels/PTFundingPanel'
    import photoPanel from '@/views/components/panels/PhotoPanel'
    import collegeCardPanel from '@/views/components/panels/CollegeCardPanel'
    import passwordPanel from '@/views/components/panels/PasswordPanel'
    import timetablePanel from '@/views/components/panels/TimetablePanel'
    import inductionHubPanel from '@/views/components/panels/InductionHubPanel'
    import myDALifePanel from '@/views/components/panels/MyDALifePanel'

    import rightNavigation from '@/views/components/panels/RightNavigation'

    export default {
        mixins: [
            filtersMixin,
            constantsMixin,
            camsConstantsMixin,
            appApiMixin,
            localSettingsMixin,
            studentsMixin,
            enrolmentsMixin,
            bursaryApplicationsMixin,
            enrolmentRegistrationsMixin,
            inductionAttendanceMixin,
            photosMixin,
            collegeCardsMixin,
            studentFundingMixin,
        ],
        components: {
            'courses-kicker': coursesKicker,
            'funding-kicker': fundingKicker,
            'photo-kicker': photoKicker,
            'college-card-kicker': collegeCardKicker,
            'password-kicker': passwordKicker,
            'timetable-kicker': timetableKicker,
            'induction-hub-kicker': inductionHubKicker,
            'myda-life-kicker': myDALifeKicker,

            'unite-course-panel': uniteCoursePanel,
            'connect2-course-panel': connect2CoursePanel,
            'rems-course-panel': remsCoursePanel,
            'he-funding-panel': heFundingPanel,
            'fe-funding-panel': feFundingPanel,
            'pt-funding-panel': ptFundingPanel,
            'photo-panel': photoPanel,
            'college-card-panel': collegeCardPanel,
            'password-panel': passwordPanel,
            'timetable-panel': timetablePanel,
            'induction-hub-panel': inductionHubPanel,
            'myda-life-panel': myDALifePanel,

            'right-navigation': rightNavigation,
        },
        data() {
            return {
                localSettings: {},
                collegeCardLocalSettings: {},
                studentFundingLocalSettings: {},
                student: {},
                loadingEnrolments: false,
                enrolments: [],
                bursaryApplications: [],
                enrolmentRegistrations: [],
                photoRecord: {},
                photo: {},
                collegeCard: {},
                collegeCardOptIn: {},
            }
        },
        computed: {
            ShowCoursesKicker() { return this.HasActiveUNITeEnrolments || this.HasConnect2Enrolments || this.HasActiveREMSEnrolments; },
            ShowFundingKicker() { return this.ShowHEFundingPanel || this.ShowFEFundingPanel || this.ShowPTFundingPanel; },
            ShowHEFundingPanel() { return this.HasFTEnrolments && this.HasHEEnrolments && !this.IsAcademicPartnershipOnly && !this.IsBlockReleaseOnly && !this.IsShortOnly; },
            ShowFEFundingPanel() { return this.HasFTEnrolments && this.HasFEEnrolments && !this.IsAcademicPartnershipOnly && !this.IsBlockReleaseOnly && !this.IsShortOnly; },
            ShowPTFundingPanel() { return this.HasPTEnrolments && !this.IsAcademicPartnershipOnly && !this.IsBlockReleaseOnly && !this.IsShortOnly; },
            ShowTimetablePanel() { return !this.IsShortOnly; },
        },
        mounted() {
            eventBus.$off('constants-fetch:success').$on('constants-fetch:success', () => this.LoadStudent());
            eventBus.$off('student-fetch:success').$on('student-fetch:success', () => {
                this.LoadStudentEnrolments(this.student.StudentID);
                this.LoadBursaryApplications(this.student.StudentID);
                this.LoadEnrolmentRegistrations(this.student.StudentID, this.constants.CurrentAcademicYear);
                this.LoadInductionAttendances(this.student.StudentID);
            });
            eventBus.$off('student-fetch:failed').$on('student-fetch:failed', () => eventBus.$emit('sign-in:clear-login'));
            eventBus.$off('enrolment-registrations-fetch:finished').$on('enrolment-registrations-fetch:finished', () => {
                this.LoadPhotoRecord(this.student.StudentReference);
                this.LoadPhoto(this.student.StudentReference);
                this.LoadCollegeCard(this.student.StudentReference);
                this.LoadCollegeCardOptIn(this.student.StudentID);
            });

            eventBus.$on('induction-attendance-save:success', () => this.LoadInductionAttendances(this.student.StudentID));

            eventBus.$off('photo-record-fetch:success').$on('photo-record-fetch:success', () => {
                sessionStorage.bypassRedirect = sessionStorage.bypassRedirect || false;
                if (this.HasCompletedAllRegistrations && this.PhotoVerified && !sessionStorage.bypassRedirect) {
                    this.$router.push({ path: '/redirect/student-portal' });
                }
            });

            eventBus.$off('photo-save:failed').$on('photo-save:failed', error => eventBus.$emit('alert:error', error));
            eventBus.$off('photo-save:success').$on('photo-save:success', () => {
                this.LoadPhotoRecord(this.student.StudentReference);
                this.LoadPhoto(this.student.StudentReference);
            });
            eventBus.$off('photo:save').$on('photo:save', (...args) => this.SavePhoto(this.student.StudentReference, args[0], args[1], args[2], args[3]));

            eventBus.$off('college-card-opt-in-save:success').$on('college-card-opt-in-save:success', () => this.LoadCollegeCardOptIn(this.student.StudentID));
            eventBus.$off('college-card-opt-in:save').$on('college-card-opt-in:save', () => this.SaveCollegeCardOptIn(this.student.StudentID, true));

            if (!!sessionStorage?.appToken) {
                this.loadingEnrolments = true;

                this.LoadConstants();
                this.LoadCAMSConstants();
                this.LoadLocalSettings();
                this.LoadCollegeCardsLocalSettings();
                this.LoadStudentFundingLocalSettings();
            }
        },
        methods: {
            EnrolmentRegistration(enrolmentID) { return this.enrolmentRegistrations?.find(er => er.enrolmentID == enrolmentID) || {}; },
        }
    }
</script>
